import {
    ApplicationRef,
    ChangeDetectorRef,
    Directive,
    Injector,
    Input,
    NgModuleRef,
    OnDestroy,
    OnInit,
    ViewContainerRef,
    createNgModule,
} from "@angular/core";
import { of, Subscription, timer } from "rxjs";
import { filter, first, switchMap, take } from "rxjs/operators";

import { Context, StorageManager, StorageService } from "@hermes/app-core";
import {
    MEDIUM_POSITION,
    LivechatButtonComponent,
    LivechatModule,
    Position,
} from "@hermes/fragments/livechat";
import { ENABLE_LIVECHAT, FeatureFlagFacade } from "@hermes/states/flipper";

@Directive({
    selector: "[hLiveChat],h-livechat",
})
export class LivechatDirective implements OnInit, OnDestroy {
    private static singletonModuleRef: NgModuleRef<LivechatModule>;

    @Input()
    public pageType!: string;

    @Input()
    public position: Position = MEDIUM_POSITION;

    private sessionStorage!: StorageManager;
    private timerLivechat = 3000;

    private moduleLoadingSubscription: Subscription = new Subscription();

    constructor(
        private injector: Injector,
        private featureFlagFacade: FeatureFlagFacade,
        private viewContainer: ViewContainerRef,
        private changeDetector: ChangeDetectorRef,
        private storageService: StorageService,
        private context: Context,
        private appReference: ApplicationRef,
    ) {
        this.sessionStorage =
            this.storageService.getSessionStorageInstance() as StorageManager;
    }

    public ngOnInit(): void {
        if (this.context.isInServerMode()) {
            return;
        }

        if (this.sessionStorage.getItem("livechatLoaded")) {
            this.timerLivechat = 0;
        }

        this.moduleLoadingSubscription = this.featureFlagFacade
            .isActivated(ENABLE_LIVECHAT)
            .pipe(
                take(1),
                filter(Boolean),
                switchMap(() => {
                    if (this.timerLivechat === 0) {
                        // No timer, no need to wait for the app to be stable
                        return of(true);
                    }
                    return this.appReference.isStable.pipe(
                        first(Boolean),
                        switchMap(() => timer(this.timerLivechat)),
                    );
                }),
            )
            .subscribe(() => {
                this.loadModule();
                if (this.moduleLoadingSubscription) {
                    this.moduleLoadingSubscription.unsubscribe();
                }
            });
    }

    public ngOnDestroy(): void {
        if (this.moduleLoadingSubscription) {
            this.moduleLoadingSubscription.unsubscribe();
        }
    }

    public async loadModule(): Promise<void> {
        if (!LivechatDirective.singletonModuleRef) {
            const module = await import("@hermes/fragments/livechat");
            LivechatDirective.singletonModuleRef = createNgModule(
                module.LivechatModule,
                this.injector,
            );
        }
        const reference = this.viewContainer.createComponent(
            LivechatButtonComponent,
            {
                injector: LivechatDirective.singletonModuleRef.injector,
                ngModuleRef: LivechatDirective.singletonModuleRef,
            },
        );

        // Pass the pagetype to the livechatButton instance
        reference.instance.pageType = this.pageType;
        // Pass the position input
        reference.instance.position = this.position;
        this.changeDetector.detectChanges();
        this.sessionStorage.setItem("livechatLoaded", 1);
    }
}
